'use client';

import React, { useState } from 'react';
import {
	Dialog,
	DialogHeader,
	DialogHeaderContent,
	DialogMain,
} from '../../../shared/components/dialog';
import { StyledDialogContent } from '../../../shared/components/dialog/DialogContent';
import styled from '@emotion/styled';
import Responsive from '../../../../ui/utils/Responsive';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import { ErrorBar } from '../../../shared/components';
import { VehiclesForm } from '../domain/helpers/VehiclesForm';
import { Section } from '../../../content-sections/ui/components/Section';
import { IVehicle } from '../../../shared/api/gateway/models/Vehicle';
import { CancelIcon, StyledTextFieldInputWrapper } from '../../../../ui';
import { Color } from '../../../shared/styles/Color';

interface IAddVehicleDialogProps {
	isOpen: boolean;
	closeDialog: () => void;
	onDelete?: () => void;
	currentVehicle?: IVehicle;
	title?: string;
	handleSuccess?: () => void;
	analyticsScreenParamValue?: string;
	setSelectedVehicle?: (vehicle: IVehicle) => void;
}

export const VehicleDialog = ({
	isOpen,
	closeDialog,
	onDelete,
	currentVehicle,
	title,
	handleSuccess,
	setSelectedVehicle,
	analyticsScreenParamValue,
}: IAddVehicleDialogProps) => {
	const { t } = useTranslation();
	const [errorBarVisible, setErrorBarVisible] = useState(false);

	const showErrorBar = () => {
		setErrorBarVisible(true);
	};

	const getTitleString = () => {
		if (title) {
			return title;
		}
		if (currentVehicle) {
			return t('account_vehicles_edit_vehicle');
		}
		return t('account_vehicles_add_new_vehicle');
	};

	const dialogTitle = getTitleString();

	return (
		<StyledVehicleDialog
			isOpen={isOpen}
			onClose={closeDialog}
			ariaLabel={dialogTitle}
		>
			<StyledDialogHeader title={dialogTitle} alignTitleAndCloseButton />
			<StyledDivider />
			<StyledDialogMain>
				<VehiclesForm
					closeDialog={closeDialog}
					onDelete={onDelete}
					currentVehicle={currentVehicle}
					handleSuccess={handleSuccess}
					onError={showErrorBar}
					setSelectedVehicle={setSelectedVehicle}
					analyticsScreenParamValue={analyticsScreenParamValue}
				/>
			</StyledDialogMain>
			{errorBarVisible && (
				<StyledErrorBar onClick={() => setErrorBarVisible(false)}>
					{t('account_vehicles_save_error_label')}
					<CancelIcon />
				</StyledErrorBar>
			)}
		</StyledVehicleDialog>
	);
};

export const StyledVehicleDialog = styled(Dialog)`
	${StyledDialogContent} {
		width: 628px !important;

		@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
			width: 101vw !important;
		}
	}

	${Section} {
		display: flex;
		flex-direction: column;

		padding-bottom: 0;
		padding-top: 0;

		@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
			margin: 0;
		}
	}

	${StyledTextFieldInputWrapper} {
		display: flex;
	}
`;

export const StyledDialogMain = styled(DialogMain)`
	margin: 0;

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		max-width: none !important;
	}
`;

export const StyledErrorBar = styled(ErrorBar)`
	display: flex;
	align-items: center;
	justify-content: space-between;

	${CancelIcon} {
		cursor: pointer;

		g {
			stroke: ${Color.TextWhite};
		}
	}
`;

export const StyledDialogHeader = styled(DialogHeader)`
	h3 {
		margin: 0;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		display: flex;

		${DialogHeaderContent} {
			flex: 1;
			align-items: center;
		}
	}
`;

const StyledDivider = styled.hr`
	margin-top: 15px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-top: 20px;
	}
`;
